import { defineStore } from "pinia";
import { readonly, ref } from "vue";

import { useCaseStore } from "./case-store";
import { useBrandStore } from "./brand-store";

import { retentionService } from "@/services";

import type {
  OverrideStartDateRequest,
  RetentionReasonedRequest,
  UndeleteRequest,
  UpdateRetentionDurationRequest,
} from "@/services/RetentionService";

const RETENTION_HISTORY_PAGE_SIZE = 15;
const PENDING_ACTION_TIMEOUT_MS = 30_000;

export const THRESHOLD_EXPIRY_DANGER = 7;
export const THRESHOLD_EXPIRY_WARNING = 30;

export const THRESHOLD_RECOVERY_DANGER = 1;
export const THRESHOLD_RECOVERY_WARNING = 7;

const getRetentionDuration = retentionService.getRetentionDuration;
const getRetentionStatus = (client: string, brand: string, casename: string) =>
  retentionService.getRetentionStatus(client, brand, casename).then((r) => ({
    ...r,
    createdAt: new Date(r.createdAt),
    requestedAt: r.requestedAt == null ? null : new Date(r.requestedAt),
  }));

const getRetentionHistory = (client: string, brand: string, casename: string, pageNumber: number) =>
  retentionService.getRetentionHistory(client, brand, casename, pageNumber, RETENTION_HISTORY_PAGE_SIZE).then((r) => ({
    ...r,
    items: r.items.map((i) => ({ ...i, createdAt: new Date(i.createdAt) })),
    pageSize: RETENTION_HISTORY_PAGE_SIZE,
  }));

export const useRetentionStore = defineStore(
  "retention",
  () => {
    const caseStore = useCaseStore();
    const brandStore = useBrandStore();

    const state = ref<
      | { status: "loading" }
      | { status: "error" }
      | {
          status: "ready";
          data: {
            duration: Awaited<ReturnType<typeof getRetentionDuration>>;
            status: Awaited<ReturnType<typeof getRetentionStatus>>;
            history: {
              requestedPage: number;
              isLoading: boolean;
              data: Awaited<ReturnType<typeof getRetentionHistory>> | null;
            };
          };
        }
    >({ status: "loading" });

    const isActionPending = ref(false);
    let pendingActionTimeoutId: ReturnType<typeof setTimeout> | null = null;

    async function load(silent = false) {
      try {
        if (brandStore.selectedClient == null || brandStore.selectedBrand == null || caseStore.selectedCaseName == null)
          throw new Error("Missing Case Route Params");

        if (!silent) state.value = { status: "loading" };

        const [historyPromise, durationStatusPromise] = await Promise.allSettled([
          loadHistory(1),
          Promise.all([
            getRetentionDuration(brandStore.selectedClient, brandStore.selectedBrand, caseStore.selectedCaseName),
            getRetentionStatus(brandStore.selectedClient, brandStore.selectedBrand, caseStore.selectedCaseName),
          ]),
        ]);
        if (durationStatusPromise.status === "rejected") throw durationStatusPromise.reason;

        const [duration, status] = durationStatusPromise.value;

        state.value = {
          status: "ready",
          data: {
            duration,
            status,
            history: {
              requestedPage: 1,
              isLoading: false,
              data: historyPromise.status === "fulfilled" ? historyPromise.value : null,
            },
          },
        };
      } catch (error) {
        console.error("Failed to load retention duration:", error);
        state.value = { status: "error" };
      }
    }

    async function loadHistory(pageNumber: number) {
      if (brandStore.selectedClient == null || brandStore.selectedBrand == null || caseStore.selectedCaseName == null)
        throw new Error("Missing Case Route Params");

      if (state.value.status === "ready") {
        if (state.value.data.history.isLoading) throw new Error("History is already loading");

        state.value.data.history.isLoading = true;
        state.value.data.history.requestedPage = pageNumber;
      }

      try {
        const rows = await getRetentionHistory(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          caseStore.selectedCaseName,
          pageNumber,
        );

        if (state.value.status === "ready" && state.value.data.history.requestedPage === rows.page) {
          state.value.data.history.data = rows;
        }

        return rows;
      } finally {
        if (state.value.status === "ready" && state.value.data.history.requestedPage === pageNumber) {
          state.value.data.history.isLoading = false;
        }
      }
    }

    function clearPendingAction() {
      if (pendingActionTimeoutId != null) {
        clearTimeout(pendingActionTimeoutId);
        pendingActionTimeoutId = null;
      }

      isActionPending.value = false;
    }

    function setPendingAction() {
      clearPendingAction();

      isActionPending.value = true;
      pendingActionTimeoutId = setTimeout(() => {
        isActionPending.value = false;
        pendingActionTimeoutId = null;
      }, PENDING_ACTION_TIMEOUT_MS);
    }

    function performAction<P = undefined>(action: (...args: [string, string, string, P]) => Promise<unknown>) {
      return async (payload: P) => {
        if (brandStore.selectedClient == null || brandStore.selectedBrand == null || caseStore.selectedCaseName == null)
          return;

        clearPendingAction();

        try {
          await action(brandStore.selectedClient, brandStore.selectedBrand, caseStore.selectedCaseName, payload);
          setPendingAction();
          await load(); // load forcefully, unmounting state in the UI
        } catch (error) {
          clearPendingAction();
          await load(true); // load silently
          throw error;
        } finally {
          const { restartPolling } = await import("./plugins/polling-plugin");
          restartPolling();
        }
      };
    }

    function reset() {
      clearPendingAction();
      state.value = { status: "loading" };
    }

    return {
      state,
      load,
      loadHistory,
      reset,
      clearPendingAction,
      isActionPending: readonly(isActionPending),
      updateDuration: performAction<UpdateRetentionDurationRequest>(retentionService.updateRetentionDuration),
      overrideStartDate: performAction<OverrideStartDateRequest>(retentionService.overrideStartDate),
      cancelDeletion: performAction<RetentionReasonedRequest>(retentionService.cancelDeletionRequest),
      cancelLegalHold: performAction<RetentionReasonedRequest>(retentionService.cancelLegalHold),
      requestDeletion: performAction<RetentionReasonedRequest>(retentionService.requestDeletion),
      applyLegalHold: performAction<RetentionReasonedRequest>(retentionService.applyLegalHold),
      recoverCase: performAction<UndeleteRequest>(retentionService.recoverCase),
      approveDeletion: performAction(retentionService.approveDeletionRequest),
    };
  },
  { scope: "case" },
);
